import BackButton from "@/components/BackButton"
import Page from "@/components/Page"
import {
  useCreateDeviceLinkSupportRequestMutation,
  useLinkDeviceViaBarcodeMutation,
  type LinkDeviceResult,
} from "@/data/queries"
import { backendErrorCodeFor, type BackendErrorCode } from "@/model/BackendErrors"
import { useBarcodeScanner } from "@/util/hooks/useBarcodeScanner"
import { useCameraStream } from "@/util/hooks/useCameraStream"
import { useState } from "react"
import BarcodeScanner from "../components/BarcodeScanner"
import ContactSupportLink from "../components/ContactSupportLink"
import LinkDeviceModal, { type LinkDeviceModalProps } from "../components/LinkDeviceModal"
import {
  cameraMessageFor,
  deviceLinkedMessageFor,
  deviceLinkErrorMessageFor,
  scannerUnavailableMessage,
  supportContactedMessage,
  type DeviceLinkResolution,
} from "../scan-device-messages"

type ScanResult =
  | { status: "linked"; outcome: LinkDeviceResult; imei: string }
  | { status: "failed"; errorCode: BackendErrorCode; imei: string }

type ScanDeviceStepProps = {
  patientId: string
  onLinked: () => void
  onCheckAddress: () => void
  onBack: () => void
}

const ScanDeviceStep = ({ patientId, onLinked, onCheckAddress, onBack }: ScanDeviceStepProps) => {
  const { videoRef, status: cameraStatus, retry: retryCamera } = useCameraStream()
  const { mutate: linkDevice, isPending: isLinking } = useLinkDeviceViaBarcodeMutation()
  const {
    mutate: contactSupport,
    isPending: isContactingSupport,
    isSuccess: hasContactedSupport,
    reset: resetContactSupport,
  } = useCreateDeviceLinkSupportRequestMutation()
  const [result, setResult] = useState<ScanResult | null>(null)
  const [isScannerUnavailable, setIsScannerUnavailable] = useState(false)

  const handleDetected = (imei: string) => {
    linkDevice(
      { patientId, imei },
      {
        onSuccess: (outcome) => setResult({ status: "linked", outcome, imei }),
        onError: (error) =>
          setResult({
            status: "failed",
            errorCode: backendErrorCodeFor(error) ?? "UNKNOWN_ERROR",
            imei,
          }),
      },
    )
  }

  useBarcodeScanner({
    videoRef,
    enabled: cameraStatus === "active" && !isLinking && result === null && !isScannerUnavailable,
    onDetected: handleDetected,
    onUnavailable: () => setIsScannerUnavailable(true),
  })

  const handleScanAgain = () => {
    setResult(null)
    resetContactSupport()
  }

  const handleContactSupport = () => {
    if (result?.status !== "failed") return
    contactSupport({ patientId, imei: result.imei, errorCode: result.errorCode })
  }

  const errorActionsFor = (resolution: DeviceLinkResolution): LinkDeviceModalProps["actions"] => {
    const contactSupportAction = {
      label: "Contact Support",
      onClick: handleContactSupport,
      isLoading: isContactingSupport,
    }
    switch (resolution) {
      case "scan-again":
        return [
          { label: "Scan again", onClick: handleScanAgain },
          { ...contactSupportAction, secondary: true },
        ]
      case "check-address":
        return [{ label: "Check shipping address", onClick: onCheckAddress }]
      case "contact-support":
        return [contactSupportAction]
    }
  }

  const currentModal = (): Omit<LinkDeviceModalProps, "open"> | null => {
    if (result?.status === "linked") {
      return {
        variant: "success",
        ...deviceLinkedMessageFor(result.outcome, result.imei),
        actions: [{ label: "Continue", onClick: onLinked }],
      }
    }
    if (result?.status === "failed" && hasContactedSupport) {
      return {
        variant: "success",
        ...supportContactedMessage,
        actions: [{ label: "Got it", onClick: handleScanAgain }],
      }
    }
    if (result?.status === "failed") {
      const { title, description, resolution } = deviceLinkErrorMessageFor(result.errorCode)
      return { variant: "error", title, description, actions: errorActionsFor(resolution) }
    }
    if (cameraStatus === "denied" || cameraStatus === "unavailable") {
      return {
        variant: "warning",
        ...cameraMessageFor(cameraStatus),
        actions: [{ label: "Try again", onClick: retryCamera }],
        showHelpFooter: true,
      }
    }
    if (isScannerUnavailable) {
      return {
        variant: "warning",
        ...scannerUnavailableMessage,
        actions: [{ label: "Try again", onClick: () => setIsScannerUnavailable(false) }],
        showHelpFooter: true,
      }
    }
    return null
  }

  const modal = currentModal()

  return (
    <Page showAppbar={false}>
      <div className="flex min-h-page flex-col gap-6">
        <BackButton customClickHandler={onBack} />
        <div className="flex flex-1 flex-col gap-6">
          <h1 className="text-h1">Link your device</h1>
          <p className="text-base text-primary-contrast/70">
            Hold your phone about 15 cm away until the barcode fills the frame.
          </p>
          <BarcodeScanner
            videoRef={videoRef}
            cameraStatus={cameraStatus}
            isLinking={isLinking}
            onRetryCamera={retryCamera}
          />
        </div>
        <ContactSupportLink />
      </div>

      {modal && <LinkDeviceModal open {...modal} />}
    </Page>
  )
}

export default ScanDeviceStep
